/**
 * This is a base component of an html5 button.
 * @author Hampus Lindholm & Anton Pedersen
 * @version 1.0
 */

import Link from "core-web/components/Link";
import PropTypes from "prop-types";
import React from "react";
import styled from "theme/libs/styled";

const ButtonElement = styled("button")`
    overflow: visible;

    &:disabled,
    &[disabled] {
        cursor: not-allowed;
    }
`;

/**
 * @param {string} ariaLabel - MUST be provided when button-text is non descriptive or Button-Element doesn't contain any text at all (ex icons).
 * @param {node} children -  JSX children.
 * @param {bool} disabled - Option to disable the button.
 * @param {string} name -  Adds a name to the button.
 * @param {string|func} onClick - Adds a function that runs on click. If it is a string link will be used instead.
 * @param {string} to - Link to internal or external url. Acts like a href.
 * @param {string} [type=button] - Specifies the type e.g. button, reset and submit.
 * @param {string} value - Adds an initial value for the button.
 * @param {string} isvolatile - If true, the component is rendered as a link and the to-prop is false the component will only render its children.
 */

const Button = ({ ariaLabel, children, disabled, name, onClick, to, type, value, isvolatile, ...rest }) => {
    // Check if onClick is a string because it can be a string or a function
    const onClickIsString = typeof onClick === "string";
    // Check if a link exists to decide if Link or Button should be used
    const renderAsLink = to || onClickIsString;

    // Check what should be passed in as href to the link
    const linkTo = to || (onClickIsString && onClick) || "";

    return renderAsLink ? (
        <Link to={linkTo} isvolatile={isvolatile} onClick={onClick} {...rest}>
            {children}
        </Link>
    ) : (
        <ButtonElement
            aria-label={ariaLabel}
            disabled={disabled}
            name={name}
            type={type}
            value={value}
            onClick={onClick}
            {...rest}
        >
            {children}
        </ButtonElement>
    );
};

Button.propTypes = {
    ariaLabel: PropTypes.string.isRequired,
    children: PropTypes.oneOfType([PropTypes.array, PropTypes.node]),
    disabled: PropTypes.bool,
    isvolatile: PropTypes.bool,
    name: PropTypes.string,
    onClick: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
    to: PropTypes.string,
    type: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

Button.defaultProps = {
    children: undefined,
    disabled: false,
    isvolatile: true,
    name: undefined,
    onClick: undefined,
    to: "",
    type: "button",
    value: undefined,
};

export default Button;
